import React, { useState, useEffect } from "react";
import { Steps, DatePicker, Input, Button, Card } from "antd";
import dayjs from "dayjs";

const { TextArea } = Input;

const TimelineLogger = ({ data, onSave, title }) => {
  const [entries, setEntries] = useState((data && data.timeline) || []);
  const [draftDate, setDraftDate] = useState(null);
  const [draftNote, setDraftNote] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const sortEntries = (list) =>
    [...list].sort(
      (a, b) =>
        dayjs(a.date, "YYYY-MM-DD HH:mm").valueOf() -
        dayjs(b.date, "YYYY-MM-DD HH:mm").valueOf()
    );

  useEffect(() => {
    if (data && data.timeline && Array.isArray(data.timeline)) {
      setEntries(sortEntries(data.timeline));
    }
  }, [data]);

  const handleAdd = () => {
    if (!draftDate || !draftNote.trim()) return;

    const newEntry = {
      date: draftDate.format("YYYY-MM-DD HH:mm"),
      note: draftNote.trim(),
    };

    if (editingIndex !== null) {
      const updated = [...entries];
      updated[editingIndex] = newEntry;
      setEntries(sortEntries(updated));
    } else {
      setEntries(sortEntries([...entries, newEntry]));
    }

    setDraftDate(null);
    setDraftNote("");
    setEditingIndex(null);
  };

  const handleRemove = (index) => {
    const updated = entries.filter((_, i) => i !== index);
    setEntries(sortEntries(updated));
    if (editingIndex === index) {
      setEditingIndex(null);
      setDraftDate(null);
      setDraftNote("");
    }
  };

  const handleSave = () => {
    onSave && onSave(entries);
    setEditMode(false);
  };

  const startEditEntry = (index) => {
    const entry = entries[index];
    if (!entry) return;
    setDraftDate(dayjs(entry.date, "YYYY-MM-DD HH:mm"));
    setDraftNote(entry.note || "");
    setEditingIndex(index);
  };

  const toggleEditMode = () => {
    setEditMode((prev) => {
      const next = !prev;
      if (!next) {
        setEditingIndex(null);
        setDraftDate(null);
        setDraftNote("");
      }
      return next;
    });
  };

  const addOrUpdateLabel =
    editingIndex !== null ? "Update Entry" : "Add Entry";

  return (
    <div className="w-[100%] flex justify-center">
      <Card
        className="w-full mt-3"
        title={title}
        bodyStyle={{ padding: 16 }}
        extra={
          <Button
            size="small"
            className="rounded-full bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300"
            onClick={toggleEditMode}
          >
            {editMode ? "Close Edit" : "Edit"}
          </Button>
        }
      >
        <div className="flex flex-col md:flex-row gap-4">
          {/* LEFT: TIMELINE */}
          <div className={`w-full ${editMode ? "md:w-2/3" : "md:w-full"}`}>
            <div className="w-full overflow-x-auto pb-2">
              <div className="w-full flex justify-center">
                {entries.length > 0 ? (
                  <div className="inline-flex px-2">
                    <Steps
                      current={entries.length - 1}
                      progressDot={() => (
                        <span className="inline-block w-2 h-2 rounded-full bg-slate-600" />
                      )}
                      responsive={false}
                      style={{ minWidth: "max-content" }}
                      items={entries.map((item, index) => ({
                        title: (
                          <div className="text-[11px] font-medium text-slate-700">
                            {dayjs(item.date).format("DD MMM YYYY")}
                          </div>
                        ),
                        description: (
                          <div className="relative text-[13px] leading-tight text-slate-600 bg-slate-50 rounded-md px-2 py-2 border border-slate-200 mr-3 mb-2">
                            {editMode && (
                                <button
                                    type="button"
                                    className="absolute right-1 top-1 text-[10px] text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full w-4 h-4 flex items-center justify-center"
                                    onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove(index);
                                    }}
                                >
                                    ×
                                </button>
                                )
                            }
                            <div className="mb-1">
                              {dayjs(item.date).format("hh:mm A")}
                            </div>
                            <div className="break-words">
                              {item.note || "-"}
                            </div>

                            {editMode && (
                              <div className="mt-1 flex justify-end">
                                <button
                                  type="button"
                                  className="text-[10px] text-emerald-600 hover:text-emerald-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditEntry(index);
                                  }}
                                >
                                  Edit
                                </button>
                              </div>
                            )}
                          </div>
                        ),
                      }))}
                    />
                  </div>
                ) : (
                  <div className="text-xs italic text-slate-400 px-2 text-center">
                    No entries yet. Turn on edit and add a date &amp; time.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: FORM (≈30%) – only in edit mode */}
          {editMode && (
            <div className="w-full md:w-1/3">
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                <div className="text-xs font-semibold text-slate-600 mb-2">
                  {addOrUpdateLabel}
                </div>

                <div className="flex flex-col gap-2">
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    className="w-full"
                    placeholder="Date & Time"
                    value={draftDate}
                    onChange={(value) => setDraftDate(value)}
                  />

                  <TextArea
                    rows={3}
                    placeholder="Note"
                    value={draftNote}
                    onChange={(e) => setDraftNote(e.target.value)}
                  />

                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      size="small"
                      onClick={() => {
                        setDraftDate(null);
                        setDraftNote("");
                        setEditingIndex(null);
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      onClick={handleAdd}
                      disabled={!draftDate || !draftNote.trim()}
                    >
                      {editingIndex !== null ? "Update Timeline" : "Add Timeline"}
                    </Button>
                  </div>

                  <div className="flex justify-end pt-1">
                    <Button size="small" type="primary" onClick={handleSave}>
                      Save Timeline
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TimelineLogger;
